/**
 * Badges (brief §17.2).
 *
 * Pure, like everything in this directory: the rules are a function from a bag of counters to
 * a list of badge keys, so a test states "somebody with 49 reports" as a literal instead of
 * making forty-nine reports. Gathering the counters is `db/badges.ts`'s job.
 *
 * The social half of this app is not decoration — without it nobody opens the map after a
 * fortnight — but it is also the half most easily farmed, so several of these count something
 * that requires other people to agree.
 */

export type BadgeKey =
  | 'erstfinder'
  | 'kartograph'
  | 'spaeher'
  | 'zeuge'
  | 'treffsicher'
  | 'serie'
  | 'reisender'
  | 'duerre'
  | 'nachtaktiv'
  | 'daumenkoenig'
  | 'spielverderber'
  | 'gruendlich'
  | 'nachteule'
  | 'weltenbummler'
  | 'bingokoenig'
  | 'trinkfest';

export type Badge = {
  key: BadgeKey;
  emoji: string;
  label: string;
  /** What to do to get it, in the second person. Shown for the ones not yet earned. */
  hint: string;
};

/** Counters gathered per user. Every field is "so far", never "in the last week". */
export type BadgeStats = {
  /** Reports filed, hidden ones excluded. */
  sightings: number;
  /** Attribute ratings given — the same number the main leaderboard counts. */
  ratings: number;
  /** Confirmations and disputes this person has cast on other people's reports. */
  confirmationsGiven: number;
  /** Own reports somebody else confirmed. */
  confirmedOwn: number;
  /** Own reports somebody else disputed. */
  disputedOwn: number;
  /** Cells whose very first report was this person's. */
  cellsOpened: number;
  /** Longest run of consecutive calendar days with at least one report. */
  streakDays: number;
  /** Distinct res-5 regions reported in. */
  regions: number;
  /** Distinct districts — the res-7 cells the zone chat calls Viertel — reported in. */
  districts: number;
  /** Cells revived after thirty quiet days. */
  revivals: number;
  /** Reports made between 23:00 and 04:00. */
  nightReports: number;
  /** Pass-by cards answered, skips included. */
  swipes: number;
  /** Cards swiped "voll lame". */
  lameSwipes: number;
  /** …of which the cell's vibe agreed. */
  lameSwipesAgreed: number;
  /** Own Waben-Bingo fields that happened: two players confirmed them (amendment 20). */
  bingoFieldsCounted: number;
  /** Rounds of the drinking games taken part in — started, voted, wagered or written in. */
  drinkingRounds: number;
};

export const EMPTY_STATS: BadgeStats = {
  sightings: 0,
  ratings: 0,
  confirmationsGiven: 0,
  confirmedOwn: 0,
  disputedOwn: 0,
  cellsOpened: 0,
  streakDays: 0,
  regions: 0,
  districts: 0,
  revivals: 0,
  nightReports: 0,
  swipes: 0,
  lameSwipes: 0,
  lameSwipesAgreed: 0,
  bingoFieldsCounted: 0,
  drinkingRounds: 0,
};

type Rule = Badge & { earned: (stats: BadgeStats) => boolean };

/**
 * The twelve brief §17.2 lists, exactly as it lists them, then the four amendment 22 adds.
 *
 * Order is the display order. Keys are permanent — they are stored in `badges.badge`, so
 * renaming one silently un-awards it for everybody who has it.
 */
const RULES: readonly Rule[] = [
  {
    key: 'erstfinder',
    emoji: '🥇',
    label: 'Erstfinder',
    hint: 'Melde als Erster etwas in einer leeren Wabe.',
    earned: (s) => s.cellsOpened >= 1,
  },
  {
    key: 'kartograph',
    emoji: '🗺️',
    label: 'Kartograph',
    hint: 'Erschließe 10 neue Waben.',
    earned: (s) => s.cellsOpened >= 10,
  },
  {
    key: 'spaeher',
    emoji: '🔎',
    label: 'Späher',
    hint: 'Setze 50 Meldungen ab.',
    earned: (s) => s.sightings >= 50,
  },
  {
    key: 'zeuge',
    emoji: '✅',
    label: 'Zeuge',
    hint: 'Bestätige 25-mal die Meldung von jemand anderem.',
    earned: (s) => s.confirmationsGiven >= 25,
  },
  {
    key: 'treffsicher',
    emoji: '🎯',
    label: 'Treffsicher',
    hint: '10 eigene Meldungen bestätigt — und keine widersprochen.',
    // The second half is what makes it worth having: it cannot be reached by volume, only by
    // being right every time.
    earned: (s) => s.confirmedOwn >= 10 && s.disputedOwn === 0,
  },
  {
    key: 'serie',
    emoji: '🔥',
    label: 'Serie',
    hint: 'Melde an 7 Tagen hintereinander etwas.',
    earned: (s) => s.streakDays >= 7,
  },
  {
    key: 'reisender',
    emoji: '🌍',
    label: 'Reisender',
    hint: 'Melde in 5 verschiedenen Gegenden.',
    earned: (s) => s.regions >= 5,
  },
  {
    key: 'duerre',
    emoji: '🌵',
    label: 'Dürre-Chronist',
    hint: 'Wecke eine Wabe auf, in der 30 Tage nichts los war.',
    earned: (s) => s.revivals >= 1,
  },
  {
    key: 'nachtaktiv',
    emoji: '🦉',
    label: 'Nachtaktiv',
    hint: '10 Meldungen zwischen 23:00 und 04:00.',
    earned: (s) => s.nightReports >= 10,
  },
  {
    key: 'daumenkoenig',
    emoji: '👍',
    label: 'Daumenkönig',
    hint: 'Beurteile 100 Vorbeikommen-Karten.',
    earned: (s) => s.swipes >= 100,
  },
  {
    key: 'spielverderber',
    emoji: '💤',
    label: 'Spielverderber',
    hint: '25-mal „voll lame" — und 20-mal lag die Mehrheit genauso.',
    // Needing the agreement is the point. Otherwise the badge rewards swiping left at
    // everything, which is the behaviour it would be silly to hand out a prize for.
    earned: (s) => s.lameSwipes >= 25 && s.lameSwipesAgreed >= 20,
  },
  {
    key: 'gruendlich',
    emoji: '📋',
    label: 'Gründlich',
    hint: 'Gib 500 Attributbewertungen ab.',
    earned: (s) => s.ratings >= 500,
  },
  {
    key: 'nachteule',
    emoji: '🌙',
    label: 'Nachteule',
    hint: '50 Meldungen zwischen 23:00 und 04:00.',
    // The step after Nachtaktiv, on the same clock: ten nights out make that one, fifty this.
    earned: (s) => s.nightReports >= 50,
  },
  {
    key: 'weltenbummler',
    emoji: '🧭',
    label: 'Weltenbummler',
    hint: 'Melde in 5 verschiedenen Vierteln.',
    earned: (s) => s.districts >= 5,
  },
  {
    key: 'bingokoenig',
    emoji: '🎱',
    label: 'Bingo-König',
    hint: 'Drei deiner Bingo-Felder treten ein, von zwei bestätigt.',
    // Fields that happened, not fields written: anybody can fill their two slots, and only a
    // good guess about the evening is worth a crown.
    earned: (s) => s.bingoFieldsCounted >= 3,
  },
  {
    key: 'trinkfest',
    emoji: '🍻',
    label: 'Trinkfest',
    hint: 'Spiel in 10 Runden der Trinkspiele mit.',
    earned: (s) => s.drinkingRounds >= 10,
  },
];

export const BADGES: readonly Badge[] = RULES.map(({ key, emoji, label, hint }) => ({
  key,
  emoji,
  label,
  hint,
}));

export function badgeFor(key: string): Badge | undefined {
  return BADGES.find((badge) => badge.key === key);
}

/** Every badge these counters earn. Awarding is monotone — nothing here takes one away. */
export function earnedBadges(stats: BadgeStats): BadgeKey[] {
  return RULES.filter((rule) => rule.earned(stats)).map((rule) => rule.key);
}

/**
 * What is newly earned, given what is already held.
 *
 * The caller stores the result; this function decides. Splitting it that way is what lets the
 * awarding job be "read counters, diff, insert" with no rule in it — and it is why a badge is
 * never revoked: `treffsicher` would otherwise vanish the first time somebody disputed an old
 * report, which is a strange thing to have happen to a trophy.
 */
export function newlyEarned(stats: BadgeStats, held: readonly string[]): BadgeKey[] {
  const already = new Set(held);
  return earnedBadges(stats).filter((key) => !already.has(key));
}

export const NIGHT_FROM_HOUR = 23;
export const NIGHT_TO_HOUR = 4;
/** Days of quiet after which a new report counts as a revival (brief §17.2). */
export const DROUGHT_DAYS = 30;
